import type { CSSProperties } from "react";
import type { ImageElementProps } from "./types";
import { resolveDynamicColor, normalizePadding } from "./utils";

const DEFAULT_FONT_FAMILY = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export interface ProfileImageElementProps extends ImageElementProps {
  /** Additional CSS class name */
  className?: string;
  /** Container style overrides */
  containerStyle?: CSSProperties;
}

/**
 * ProfileImage - Renders a circular profile image with optional overlay.
 * Used in both admin template builder and user-facing workspace.
 * Handles missing images with a fallback initial.
 */
export function ProfileImageElement({
  src,
  alt,
  fallbackContent,
  brandColors,
  templateDefaults,
  style,
  size,
  opacity,
  overlayColor,
  overlayOpacity,
  imageFit = "cover",
  borderRadius,
  borderWidth,
  borderColor,
  shadow,
  className,
  containerStyle,
}: ProfileImageElementProps) {
  const resolvedBorderColor = borderColor
    ? resolveDynamicColor(borderColor, templateDefaults, brandColors, borderColor)
    : style?.borderColor
      ? resolveDynamicColor(style.borderColor, templateDefaults, brandColors, style.borderColor)
      : undefined;
  
  const resolvedOverlayColor = overlayColor
    ? resolveDynamicColor(overlayColor, templateDefaults, brandColors, overlayColor)
    : style?.overlayColor
      ? resolveDynamicColor(style.overlayColor, templateDefaults, brandColors, style.overlayColor)
      : undefined;
  
  const padding = normalizePadding(style);
  
  const containerCss: CSSProperties = {
    position: "relative",
    width: "100%",
    height: "100%",
    overflow: "hidden",
    borderRadius: borderRadius ?? style?.borderRadius ? `${borderRadius ?? style?.borderRadius}px` : undefined,
    borderWidth: borderWidth ?? style?.borderWidth ? `${borderWidth ?? style?.borderWidth}px` : undefined,
    borderColor: resolvedBorderColor,
    borderStyle: (borderWidth ?? style?.borderWidth) ? "solid" : undefined,
    boxShadow: shadow ?? style?.shadow ?? undefined,
    opacity: opacity ?? style?.opacity ?? 1,
    padding,
    ...containerStyle,
  };
  
  const imageCss: CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: imageFit ?? style?.imageFit ?? "cover",
    display: "block",
  };
  
  const overlayCss: CSSProperties = resolvedOverlayColor ? {
    position: "absolute",
    inset: 0,
    backgroundColor: resolvedOverlayColor,
    opacity: overlayOpacity ?? style?.overlayOpacity ?? 0.3,
    pointerEvents: "none",
  } : {};
  
  const fallbackInitial = fallbackContent?.[0]?.toUpperCase() ?? "R";
  
  return (
    <div className={className} style={containerCss}>
      {src ? (
        <>
          <img src={src} alt={alt} style={imageCss} />
          {resolvedOverlayColor && <div style={overlayCss} />}
        </>
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            backgroundColor: brandColors.accent,
            color: "#fff",
            fontFamily: DEFAULT_FONT_FAMILY,
            fontSize: "32px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textTransform: "uppercase",
          }}
        >
          {fallbackInitial}
        </div>
      )}
    </div>
  );
}
